/**
 ShapeDiver 3D Viewer Application

 MaterialParser.js

 Content:
   Logic to parse the incoming JSONMaterials into desired format.

  Copyright (c) 2018 ShapeDiver GmbH

  @module MaterialParser
 */

/**
 Import THREE
 @ignore
*/
const THREE = require('../../externals/three');

const MATERIAL_PRESETS = require('./MaterialPresets');

/**
 * Utility function to convert from all kinds of string formats to a tinycolor
 * @function
 * @ignore
 */
var toTinyColor = require('../util/toTinyColor');

/**
 * Texture loader function with built in caching
 * @function
 * @ignore
 */
var loadTexture = require('../singletons/TextureLoader').loadTexture;

/**
 * Global utilities
 */
var GlobalUtils = require('../util/GlobalUtils');

var MaterialPropertiesStandard = require('../materials/MaterialPropertiesStandard');
var MaterialPropertiesGem = require('../materials/MaterialPropertiesGem');

// from JSON
var MaterialParser = function () {

  let _loadTextureFromJSON = function (jsonTexDef) {

    if (typeof jsonTexDef === 'string') {
      jsonTexDef = { href: jsonTexDef };
    }
    if (!jsonTexDef.hasOwnProperty('href') && !jsonTexDef.hasOwnProperty('canvas')) {
      return Promise.reject(new Error('Input object is not a valid JSON texture definition.'));
    }

    let options = {};
    options.offset = jsonTexDef.offset || [0, 0];
    options.repeat = jsonTexDef.repeat || [1, 1];
    options.rotation = jsonTexDef.rotation || 0;
    options.center = jsonTexDef.center || [0, 0];
    options.color = jsonTexDef.color || null;

    return loadTexture(jsonTexDef.href || jsonTexDef.canvas, options)
      .then(
        function (texture) {
          if (options.color) {
            let t = toTinyColor(options.color);
            if (t && t.isValid()) {
              texture.SDColor = t;
            } else {
              //TODO: Warn about formatting error
            }
          }
          return (texture);
        },
        function (err) {
          return Promise.reject(err);
        }
      );
  };

  /**
   * Initialize this object from model view interface material defintion of version 2.0
   * @private
   * @param  {Object} materialDef Model view material definition, version 2.0
   * @param {String} version Version string, currently one of '1.0', '2.0' or '3.0'
   * @return {Promise<MaterialAttributes>}    Pointer to this object if successful.
   */
  var _processStandardMaterial = function (materialDef, version) {

    let supportedVersions = ['1.0', '2.0', '3.0'];
    if (supportedVersions.indexOf(version) == -1) {
      return Promise.reject(new Error('Material definition has unknown version.'));
    }

    let m = new MaterialPropertiesStandard();

    if(version === '1.0') {
      if (materialDef.shine != null) materialDef.metalness = Math.min(1, materialDef.shine);
      if (materialDef.shine != null) materialDef.roughness = 1 - (Math.min(1, materialDef.shine));
      if (materialDef.hasOwnProperty('diffuse') && !materialDef.hasOwnProperty('color')) materialDef.color = materialDef.diffuse;
    }

    // floats
    [ 'metalness', 'roughness', 'alphaThreshold', 'shadowOpacity', 'lightReflectivity',
      'transparency', 'bumpAmplitude'
    ].forEach(function(attr) {
      if(materialDef.hasOwnProperty(attr)){
        let n = parseFloat(materialDef[attr]);
        if (!isNaN(n) && isFinite(n)){
          switch(attr) {
            case 'transparency':
              m.opacity = 1 - n;
              m.transparent = n>0;
              break;
            case 'bumpAmplitude':
              m.bumpScale = n;
              break;
            default:
              m[attr] = n;
          }
        }
      }
    });

    // color
    [ 'color', ].forEach(function(attr) {
      if(materialDef.hasOwnProperty(attr)){
        let tc = toTinyColor(materialDef[attr]);
        if (tc && tc.isValid())
          m[attr] = tc.toThreeColor();
      }
    });

    // 3D noise properties (only from 3.0 onwards)

    if (materialDef.hasOwnProperty('threeDNoise')) {
      m.threeDNoiseOpacity = materialDef.threeDNoise.opacity || 0.0;
      m.threeDNoiseID = materialDef.threeDNoise.noiseID || 0;
      m.threeDNoiseScale = materialDef.threeDNoise.scale || 1.0;
      m.threeDNoiseDistanceFade = materialDef.threeDNoise.distance || 0.0;
    }



    // textures
    let promises = [];

    if (materialDef.hasOwnProperty('bitmaptexture')) {

      // in version 3.0, we might get an array of color textures
      // TODO SS-870 document this
      if (Array.isArray(materialDef.bitmaptexture)) {
        if (materialDef.bitmaptexture.length === 0) {
          return Promise.resolve(null);
        }
        let colorTexPromiseArray = [];
        for (let tex of materialDef.bitmaptexture) {
          colorTexPromiseArray.push(_loadTextureFromJSON(tex));
        }
        promises.push(Promise.all(colorTexPromiseArray)
          .then(
            function (textures) {
              if (textures.length > 1) {
                m.additionalMaps = [];
                m.mapPropertyType = [];
                m.mapPropertyColor = [];
                m.uvTransformAddMap = [];
                for (let i = 1, iBound = textures.length; i < iBound; ++i) {
                  let tex = textures[i];
                  m.additionalMaps.push(tex);
                  m.mapPropertyType.push(tex.hasOwnProperty('SDColor') ? 1 : 0);
                  let c = tex.hasOwnProperty('SDColor') && tex.SDColor ? tex.SDColor.toThreeColor() : new THREE.Color();
                  m.mapPropertyColor.push(c);
                  m.uvTransformAddMap.push(tex.uvTransform);
                }
              }
              return m.map = textures[0];
            }
          ));
      } else {
        promises.push(_loadTextureFromJSON(materialDef.bitmaptexture)
          .then(
            function (texture) {
              return m.map = texture;
            }
          ));
      }
    }


    [ 'metalness', 'roughness', 'bump', 'normal', 'transparency' ].forEach(function(attr) {
      if(materialDef.hasOwnProperty(attr+'texture')){
        promises.push(_loadTextureFromJSON(materialDef[attr+'texture'])
          .then(function (texture) {
            if(attr === 'transparency') {
              m.alphaMap = texture;
              m.transparent = true;
            } else {
              m[attr+'Map'] = texture;
            }
          }));
      }
    });

    return Promise.all(promises)
      .then(function () {
        m._json = materialDef;
        return m;
      });
  };

  var _processGemMaterial = function (materialDef) {
    let m = new MaterialPropertiesGem();

    // floats
    [ 'contrast', 'opacityFront', 'opacityBack', 'metalnessFront', 'metalnessBack', 'roughnessFront', 'roughnessBack',
      'reflectionNormalMapIntensity', 'reflectionNormalMapRepeat', 'dispersionMapIntensity', 'dispersionMapRepeat',
      'reflectionMapIntensityFront', 'reflectionMapRepeatFront', 'reflectionMapIntensityBack', 'reflectionMapRepeatBack',
      'directSpecularIntensity', 'indirectSpecularIntensity', 'directSpecularContrast', 'indirectSpecularContrast',
      'reflectionMapContrastFront', 'reflectionMapContrastBack'

    ].forEach(function(attr) {
      if(materialDef.hasOwnProperty(attr)){
        let n = parseFloat(materialDef[attr]);
        if (!isNaN(n) && isFinite(n))
          m[attr] = n;
      }
    });

    // booleans
    [ 'flatShading', 'transparent' ].forEach(function(attr) {
      if(materialDef.hasOwnProperty(attr)){
        m[attr] = materialDef[attr];
      }
    });

    // colors
    [ 'colorFront', 'colorBack', 'reflectionMapColorFront', 'reflectionMapColorBack' ].forEach(function(attr) {
      if(materialDef.hasOwnProperty(attr)){
        let tc = toTinyColor(materialDef[attr]);
        if (tc && tc.isValid())
          m[attr] = tc.toThreeColor();
      }
    });

    // textures
    let promises = [];
    [ 'reflectionNormalMap', 'reflectionMapFront', 'reflectionMapBack', 'dispersionMap' ].forEach(function(attr) {
      if(materialDef.hasOwnProperty(attr)){
        promises.push(_loadTextureFromJSON(materialDef[attr])
          .then(function (texture) {
            m[attr] = texture;
          }));
      }
    });

    return Promise.all(promises)
      .then(function () {
        m._json = materialDef;
        return m;
      });
  };

  /**
   * Create a material definition object from a material object as formatted in the
   * ShapeDiver model view interface.
   *
   * Supported versions: 1.0 and 2.0
   *
   * All textures will be downloaded.
   *
   * @param  {module:JSONMaterial~JSONMaterial(1)|module:JSONMaterial~JSONMaterial(2)} JSONMaterialObject Material definition in model view interface definition
   * @return {Promise<module:MaterialAttributes~MaterialAttributes>}   Pointer to this object if successful, null otherwise
   */
  this.fromJSONMaterialObject = function (JSONMaterialObject) {
    let materialType = 'standard';
    if (JSONMaterialObject.hasOwnProperty('materialType'))
      materialType = JSONMaterialObject.materialType;

    if (materialType === 'gem') {
      if (!JSONMaterialObject.hasOwnProperty('version'))
        JSONMaterialObject.version = '1.0';

      return _processGemMaterial(JSONMaterialObject);
    } else {
      if (!JSONMaterialObject.hasOwnProperty('version')) {
        return Promise.reject(new Error('Material definition did not have version number defined'));
      }

      // if we find a preset, we get the definition
      if (JSONMaterialObject.hasOwnProperty('materialpreset')) {
        let presetMaterial = GlobalUtils.deepCopy(MATERIAL_PRESETS.fromId(JSONMaterialObject.materialpreset));

        // if the color that was sent with the preset definition is white, we ignore that and keep the color
        // that was set by the preset. if there is a different color, we have to use it.
        if (JSONMaterialObject.hasOwnProperty('color') && (parseInt(JSONMaterialObject.color[0]) !== 255 || parseInt(JSONMaterialObject.color[1]) !== 255 || parseInt(JSONMaterialObject.color[2]) !== 255)) {
          presetMaterial.color = JSONMaterialObject.color;
        }

        // if there were maps specified together with the presets, we have to respect that as well
        if (JSONMaterialObject.hasOwnProperty('bitmaptexture')) {
          presetMaterial.bitmaptexture = JSONMaterialObject.bitmaptexture;
        }
        if (JSONMaterialObject.hasOwnProperty('bumptexture')) {
          presetMaterial.bumptexture = JSONMaterialObject.bumptexture;
        }

        // process the combined material definition
        // TODO SS-871 process preset material according to its version
        return _processStandardMaterial(presetMaterial, '2.0');
      }
      return _processStandardMaterial(JSONMaterialObject, JSONMaterialObject.version);
    }

  };
};

module.exports = MaterialParser;
